import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Envelope as Mail } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SiteFooter } from "../components/SiteFooter";

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  bg: "#FAF7F0",
  ink: "#2A1E5C",
  ink2: "#5D5A6B",
  muted: "#8B7E6A",
  line: "#E8E2D5",
  card: "#FFFFFF",
  gold: "#F5CB5C",
  red: "#DE3C4B",
};

const GoogleMark = () => (
  <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 0 1 9.77 24c0-1.6.28-3.14.76-4.59l-7.98-6.19A23.94 23.94 0 0 0 0 24c0 3.88.93 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentNote, setSentNote] = useState("");

  const googleSignIn = () => {
    const next = new URLSearchParams(window.location.search).get("next");
    try {
      sessionStorage.setItem("pr.auth.next", next && next.startsWith("/") ? next : "/dashboard");
    } catch { /* storage unavailable */ }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed.includes("@") || sending) return;
    setSending(true);
    try {
      const next = new URLSearchParams(window.location.search).get("next");
      const res = await fetch(`${API}/api/auth/request-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, ...(next && next.startsWith("/") ? { next } : {}) }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "Couldn't send the link, try again in a minute.");
        return;
      }
      const data = await res.json().catch(() => ({}));
      setSentNote(data.throttled ? data.message : "");
      setSent(true);
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: COLOR.bg }}>
      <header className="py-2 px-4 sm:px-6 border-b" style={{ borderColor: COLOR.line, background: COLOR.card }}>
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm hover:opacity-70" style={{ color: COLOR.ink }} data-testid="login-back-link">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <img src="/images/paydayrelaylogo-tight.png" alt="Payday Relay" className="h-[64px] sm:h-[81px] w-auto" data-testid="login-header-logo" />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full">
          {sent ? (
            <div
              className="rounded-2xl p-8 text-center border"
              style={{ background: COLOR.card, borderColor: COLOR.line }}
              data-testid="login-sent"
            >
              <h1 className="text-3xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
                Check your inbox
              </h1>
              <p className="text-sm leading-relaxed mb-6" style={{ color: COLOR.ink2 }}>
                We sent a one-tap sign-in link to <b style={{ color: COLOR.ink }}>{email.trim().toLowerCase()}</b>.
                It works once and lasts an hour.
              </p>
              {sentNote && (
                <p className="text-xs leading-relaxed mb-6" style={{ color: COLOR.muted }} data-testid="login-sent-note">
                  {sentNote}
                </p>
              )}
              <button
                onClick={() => setSent(false)}
                className="text-xs underline"
                style={{ color: COLOR.muted }}
                data-testid="login-use-different"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <div
              className="rounded-2xl p-8 border"
              style={{ background: COLOR.card, borderColor: COLOR.line }}
              data-testid="login-card"
            >
              <div className="text-center mb-6">
                <h1 className="text-3xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
                  Sign in to <span style={{ color: COLOR.red }}>Payday Relay</span>
                </h1>
                <p className="text-sm leading-relaxed" style={{ color: COLOR.ink2 }}>
                  Use Google, or have us email you a one-tap sign-in link. Either way you land in the same relay.
                </p>
              </div>
              <button
                type="button"
                onClick={googleSignIn}
                className="w-full px-6 py-3.5 rounded-full font-bold text-base inline-flex items-center justify-center gap-2.5 border transition-transform hover:scale-[1.02] mb-5"
                style={{ background: "#FFFFFF", borderColor: COLOR.line, color: COLOR.ink }}
                data-testid="login-google-btn"
              >
                <GoogleMark /> Continue with Google
              </button>

              <div className="flex items-center gap-3 mb-5" aria-hidden="true">
                <span className="flex-1 h-px" style={{ background: COLOR.line }} />
                <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: COLOR.muted }}>or</span>
                <span className="flex-1 h-px" style={{ background: COLOR.line }} />
              </div>

              <form onSubmit={submit}>
                <label className="block text-xs font-semibold mb-1" style={{ color: COLOR.ink }}>Your email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={sending}
                  className="w-full px-4 py-3 rounded-lg border text-sm mb-4 focus:outline-none"
                  style={{ borderColor: COLOR.line, background: COLOR.bg, color: COLOR.ink }}
                  data-testid="login-email-input"
                />
                <button
                  type="submit"
                  disabled={sending || !email.trim().includes("@")}
                  className="w-full px-6 py-3.5 rounded-full font-bold text-base inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] disabled:opacity-50"
                  style={{ background: COLOR.gold, color: COLOR.ink }}
                  data-testid="login-submit-btn"
                >
                  <Mail className="w-4 h-4" />
                  {sending ? "Sending…" : "Email me a sign-in link"}
                </button>
              </form>
              <p className="text-xs text-center mt-5" style={{ color: COLOR.muted }}>
                New here?{" "}
                <Link to="/start/free-sprint" className="underline" style={{ color: COLOR.ink }} data-testid="login-new-here-link">
                  Start your Free Sprint →
                </Link>
              </p>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
